// Agent console client. Same session cookie as the portal, different base path
// and a deliberately separate set of types — CLAUDE.md invariant 5 keeps the two
// APIs apart, and sharing types here would quietly re-couple them.

import { ApiError, messageFrom } from '../lib/api'

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : ''
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.method && init.method !== 'GET') {
    headers.set('X-CSRFToken', csrfToken())
    if (!(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(`/api/agent${path}`, {
    credentials: 'same-origin',
    ...init,
    headers,
  })

  if (!response.ok) {
    // One implementation, shared with the portal client. This used to read only
    // `body.detail`, so a serializer's `{"email": ["…already exists."]}` — which
    // has no `detail` — was rendered as the words "Bad Request", and every
    // admin screen threw away the explanation the server had just given.
    let body: unknown = null
    try {
      body = await response.json()
    } catch {
      /* non-JSON body */
    }
    const { detail, fields } = messageFrom(body, response.status, response.statusText)
    throw new ApiError(response.status, detail, fields)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const agentApi = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? '{}' : JSON.stringify(body) }),
  patch: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  // Team membership is set whole (PUT) rather than patched — see agent/directory.py.
  put: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  // Multipart, for attachment upload. No Content-Type header: the browser sets
  // it with the multipart boundary, and overriding it breaks the parse.
  postForm: <T,>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),
  // The KB is the only agent resource with a destroy endpoint today.
  delete: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
}

// The shape of /me/ lives in lib/authz with the helpers that read it, so the
// permission type and the payload cannot drift apart. Re-exported here because
// this is where the rest of the console looks for API types.
export type {
  AgentMe,
  AgentOrganizationScope,
  AgentRole,
  ModuleKey,
  Permission,
} from '../lib/authz'

export interface AgentSla {
  policy: string
  response_due_at: string | null
  resolution_due_at: string | null
  breached_response: boolean
  breached_resolution: boolean
  paused_since: string | null
  response_percent: number
  resolution_percent: number
}

export interface AgentTicket {
  ref: string
  type: string
  title: string
  status: string
  priority: string
  impact: string
  urgency: string
  organization: string
  requester_name: string
  requester_email: string
  service_subcategory: number | null
  service_subcategory_name: string | null
  service_id: number | null
  service_name: string | null
  assigned_agent: number | null
  assigned_agent_name: string | null
  assigned_team: number | null
  assigned_team_name: string | null
  source_channel: string
  created_at: string
  // The serializer has always returned these three; the type did not declare
  // them, which is why docs/UI-BACKLOG.md spent a revision believing the queue
  // could not support MTTR. It can — see lib/metrics.ts.
  first_response_at: string | null
  resolved_at: string | null
  closed_at: string | null
  // On the queue feed since the full-site audit. It was on the model, on the
  // portal serializer and on the agent *detail* serializer, and only the queue
  // omitted it — which is why the executive CSAT panel spent three phases
  // claiming the figure could not be derived on the client. It can; see
  // `csatStats` in lib/metrics.
  csat_score: number | null
  /** Blank means the requester rated without writing feedback. Null is accepted
   *  defensively for older payloads or imports. */
  csat_comment: string | null
  csat_submitted_at: string | null
  sla: AgentSla | null
}

export interface AgentCaseLog {
  id: number
  body_html: string
  visibility: 'public' | 'internal'
  author_name: string
  created_at: string
  attachments: AgentAttachment[]
}

/** Served on tickets and case logs since the beginning; downloadable through
 *  `/api/agent/attachments/:id/download/`, the desk-side permission-checked
 *  view. Downloads never go through nginx. */
export interface AgentAttachment {
  id: number
  filename: string
  size_bytes: number
}

/** The manager sign-off on a service request, as the desk sees it.
 *
 *  Wider than the requester's view of the same approval — it carries
 *  `blocks_fulfilment`, which is the fact an agent needs and the requester does
 *  not. `AgentTicketDetailSerializer.get_manager_approval`. */
export interface AgentManagerApproval {
  id: number
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired'
  status_label: string
  approver_name: string
  approver_email: string
  decision: string | null
  decision_source: string | null
  reason: string | null
  message: string | null
  comment: string | null
  /** Null until somebody decides. */
  decided_by: string | null
  requested_at: string
  decided_at: string | null
  expires_at: string | null
  /** True while the gate is shut. The API refuses status and assignment
   *  changes on this ticket for as long as it is. */
  blocks_fulfilment: boolean
}

export interface AgentApprovalCandidate {
  id: number
  name: string
  email: string
  is_active: boolean
}

export interface AgentTicketDetail extends AgentTicket {
  description: string
  form_data: Record<string, unknown>
  case_logs: AgentCaseLog[]
  attachments: AgentAttachment[]
  links: { id: number; link_type: string; to_ref: string; to_title: string }[]
  cis: { id: number; ci_name: string; ci_class: string; criticality: string; impact_role: string }[]
  watchers: string[]
  /** Null when the request needed no sign-off, which is most of them. */
  manager_approval: AgentManagerApproval | null
  approval_candidate: AgentApprovalCandidate | null
}

export interface ImpactedCi {
  id: number
  name: string
  ci_class: string
  criticality: string
  depth: number
  service_name: string | null
}

export interface ChangeRequestDetail {
  ref: string
  title: string
  change_class: string
  state: string
  risk: string
  reason: string
  implementation_plan: string
  backout_plan: string
  test_plan: string
  scheduled_start: string | null
  scheduled_end: string | null
  outcome: string
  pir_notes: string
  freeze_override_reason: string
  implemented_before_approval: boolean
  approvals: {
    id: number
    stage: string
    approver_name: string
    approver_email: string
    decision: string
    comment: string
  }[]
  impact_analysis: {
    cis: ImpactedCi[]
    services: { id: number; name: string }[]
    max_criticality: string
    dependent_count: number
  }
  available_transitions: string[]
  blocking_freezes: { reason: string; starts_at: string; ends_at: string }[]
}

export interface CabMeeting {
  id: number
  scheduled_at: string
  chair_name: string | null
  minutes: string
  agenda_items: {
    id: number
    ref: string
    title: string
    risk: string | null
    change_class: string | null
    outcome: string
  }[]
}

export interface Dashboard {
  volume: {
    days: number
    total: number
    open: number
    by_type: Record<string, number>
    by_status: Record<string, number>
    by_channel: Record<string, number>
  }
  sla_attainment: {
    measured: number
    response_attainment: number | null
    resolution_attainment: number | null
    breached_response: number
    breached_resolution: number
  }
  change_success_rate: {
    completed: number
    success_rate: number | null
    by_outcome: Record<string, number>
  }
  emergency_change_percentage: {
    total: number
    emergency: number
    percentage: number | null
    unapproved: number
  }
  changes_causing_incidents: {
    incidents_caused: number
    changes_implicated: number
    percentage_of_changes: number | null
    detail: { incident: string; incident_title: string; change: string }[]
  }
  breaching_now: number
}

export interface AgentArticle {
  id: number
  slug: string
  title: string
  body_md: string
  org_visibility: string
  published: boolean
  // author_name and created_at have always been on AgentArticleSerializer; the
  // type omitted them, the same way it omitted the ticket timestamps before
  // Phase C. lib/kb.ts declares the shape the KB screens actually use.
  author_name: string | null
  created_at: string
  updated_at: string
}

export interface AgentOrganization {
  id: number
  name: string
  code: string
  is_internal: boolean
  /** Offboarded tenants stay listed for the scope control — their tickets are
   *  still readable — but no picker that writes may offer one. */
  is_active: boolean
}

/* ------------------------------------------------------------- ticket feed */

export interface TicketFeedPage {
  results: AgentTicket[]
  next_cursor: string | null
  has_more: boolean
}

/** How many rows the client will pull before it stops asking.
 *
 *  The server no longer caps the feed — that was the point of building it — so
 *  the bound moved here, where it belongs: a browser holding a hundred thousand
 *  ticket objects to draw a bar chart is its own kind of broken. Ten thousand
 *  covers years of this deployment's volume (specified at under ten agents and
 *  five hundred users) and still fits comfortably in memory.
 *
 *  When it *is* hit, `fetchTicketFeed` says so and the screens print the same
 *  coverage notice they always did. The honesty machinery in `lib/metrics.ts`
 *  did not become unnecessary; it became rarely triggered. */
export const FEED_ROW_BUDGET = 10_000

/** Page size. Large, because each round trip costs more than each row. */
const FEED_PAGE = 500

export interface TicketFeedResult {
  tickets: AgentTicket[]
  /** False when the budget stopped the walk before the server ran out of rows,
   *  which is the one case a derived figure must not be trusted over. */
  complete: boolean
  /** The earliest instant from which these rows are exhaustive — every ticket
   *  created at or after it is present.
   *
   *  This is what `coverageOf()` needs and cannot work out for itself. A walk
   *  that finished is exhaustive back to the `since` it asked for; one the
   *  budget stopped is exhaustive only back to the oldest row it got, because
   *  the feed returns newest first and the rows it never reached are all older
   *  than that. Null only when the walk returned nothing at all. */
  coveredSince: Date | null
}

/** Page the uncapped feed until the window is covered or the budget is spent.
 *
 *  Replaces `GET /api/agent/tickets/?include_closed=true`, whose 200-row slice
 *  bounded every figure on Reports and the executive overview — the gap this
 *  feed was built to close.
 *
 *  `since` matters: bounding the window server-side is what keeps the walk
 *  short. Without it a report over thirty days would page the entire history to
 *  find the thirty days it wanted.
 */
export async function fetchTicketFeed(
  options: { since?: Date; organizationId?: string | null } = {},
): Promise<TicketFeedResult> {
  const tickets: AgentTicket[] = []
  let cursor: string | null = null

  /** Oldest row we actually hold. The feed pages newest first, so this is the
   *  boundary of what we can claim to have seen all of. */
  const oldestSeen = (): Date | null => {
    const last = tickets[tickets.length - 1]
    const parsed = last ? new Date(last.created_at) : null
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null
  }

  /** A finished walk is exhaustive back to the `since` we asked for — or to the
   *  beginning of the table when we asked for no bound at all. */
  const covered = (): Date | null => options.since ?? (tickets.length ? new Date(0) : null)

  for (;;) {
    const params = new URLSearchParams({ limit: String(FEED_PAGE), include_closed: 'true' })
    if (options.since) params.set('since', options.since.toISOString())
    if (options.organizationId) params.set('organization', options.organizationId)
    if (cursor) params.set('cursor', cursor)

    const page = await agentApi.get<TicketFeedPage>(`/tickets/feed/?${params}`)
    tickets.push(...page.results)

    // Budget checked before the next request, not after: checking after would
    // let one more 500-row page through and make the bound a suggestion.
    if (tickets.length >= FEED_ROW_BUDGET) {
      const complete = !page.has_more
      return { tickets, complete, coveredSince: complete ? covered() : oldestSeen() }
    }
    if (!page.next_cursor) return { tickets, complete: true, coveredSince: covered() }
    cursor = page.next_cursor
  }
}
